import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Group, Text as KonvaText, Rect as KonvaRect, Image as KonvaImage } from 'react-konva';
import Konva from 'konva';
import { toast } from 'react-toastify';
import GIF from 'gif.js';
import gifshot from 'gifshot';
import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';
import styles from './styles.module.css';


const MAX_FILE_SIZE = 5 * 1024; // 5KB in bytes

const convertToGIFBase64 = (file, maxColors, backgroundColor = '#FFFFFF', setBinarySize, updateElement, element) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      const result = event.target.result;

      // Check the file type
      const fileType = file.type;
      if (fileType === 'image/gif') {
        // Process GIF file
        const binaryData = atob(result.split(',')[1]); // Extract binary data from Base64

        if (binaryData.length > MAX_FILE_SIZE) {
          reject(new Error('The generated GIF exceeds the maximum size of 5KB. This file is too large : ' + binaryData.length + "KB"));
          return;
        }

        setBinarySize(binaryData.length); // Set binary size directly
        updateElement(element.id, 'imageData', result);
        resolve(result);
        return;
      }

      if (fileType === 'image/png') {
        // Process PNG files
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            // Apply background color
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw the image on the canvas
            ctx.drawImage(img, 0, 0);

            // Extract RGBA image data
            const imageData = ctx.getImageData(0, 0, img.width, img.height);
            const { data, width, height } = imageData;

            console.log('Image Dimensions:', width, height);

            // Quantize the image to reduce colors
            const palette = quantize(data, maxColors, { format: 'rgb565' });
            console.log('Palette:', palette);

            // Apply the palette to get an indexed bitmap
            const index = applyPalette(data, palette, 'rgb565');

            // Create a GIF encoder
            const gif = GIFEncoder();

            // Write a frame
            gif.writeFrame(index, width, height, {
              palette,
              repeat: 0, // Loop forever
              delay: 500, // Delay per frame in milliseconds
            });

            // Finish the GIF encoding
            gif.finish();

            // Get the output as a Uint8Array
            const output = gif.bytes();

            if (output.length > MAX_FILE_SIZE) {
              reject(new Error('The generated GIF exceeds the maximum size of 5KB. This file is too large : ' + output.length + "KB"));
              return;
            }

            setBinarySize(output.length);

            // Convert the Uint8Array to a Base64 string
            const base64GIF = `data:image/gif;base64,${btoa(
              String.fromCharCode(...output)
            )}`;
            console.log('GIF generated successfully.');
            resolve(base64GIF);
          } catch (err) {
            console.error('Error generating GIF:', err);
            reject(new Error('Failed to generate GIF.'));
          }
        };

        img.onerror = () => reject(new Error('Failed to load image.'));
        img.src = result; // Set the source to the Base64 image
      } else {
        reject(new Error('Unsupported file type. Only PNG and GIF are supported.'));
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file); // Convert file to Base64
  });
};

/**
* ThemeImageCustomSettings Component
* Manages settings like position and file upload.
*/
export const ThemeImageCustomSettings = ({ element, updateElement, backgroundColor }) => {
  const [binarySize, setBinarySize] = useState(0);
  const [maxColors, setMaxColors] = useState(256);
  const [lastUploadedFile, setLastUploadedFile] = useState(null);

  /**
   * Handle X position change.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x) && x >= 0) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handle Y position change.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y) && y >= 0) {
      updateElement(element.id, 'y', y);
    }
  };

  const handleMaxColorsChange = (e) => {
    const colors = parseInt(e.target.value, 10);
    if (!isNaN(colors) && colors > 0 && colors <= 256) {
      setMaxColors(colors);
    } else {
      toast.error('Max colors must be between 1 and 256.');
    }
  };

  const handleFileUpload = async (e) => {
    const fileInput = e.target; // Reference to the input element
    const file = fileInput.files[0];

    if (file) {
      // Recompute even if the same file is selected
      if (
        lastUploadedFile &&
        lastUploadedFile.name === file.name &&
        lastUploadedFile.size === file.size &&
        lastUploadedFile.dateImport === file.dateImport
      ) {
        toast.info('Recomputing for the same file...');
      }
      file.dateImport = Date.now();
      setLastUploadedFile(file); // Update the last uploaded file

      try {
        const base64Data = await convertToGIFBase64(
          file,
          maxColors,
          backgroundColor,
          setBinarySize,
          updateElement,
          element
        );
        updateElement(element.id, 'imageData', base64Data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        // Reset the file input value to allow re-upload of the same file
        fileInput.value = '';
      }
    }
  };


  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* X Position */}
      <label style={{ display: 'block', marginBottom: '5px' }}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label style={{ display: 'block', marginBottom: '5px' }}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Y Position"
        />
      </label>

      {/* Max Colors */}
      <label style={{ display: 'block', marginBottom: '5px' }}>
        Max number of colors for PNG import (2-256):
        <input
          className={styles.inputNumber}
          type="number"
          min="2"
          max="256"
          value={maxColors}
          onChange={handleMaxColorsChange}
          style={{ marginLeft: '10px' }}
          aria-label="Set Max Colors"
        />
      </label>

      {/* File Upload */}
      <label style={{ display: 'block', marginBottom: '5px' }}>
        Upload Image (PNG/GIF):
        <input
          type="file"
          accept="image/png, image/gif"
          onChange={handleFileUpload}
          onSubmit={(e) => console.log(e)}
          style={{
            marginLeft: '10px',
            padding: '6px 12px',
            borderRadius: '4px',
            border: 'none',
            color: '#ffffff',
            cursor: 'pointer',
            width: '250px',
          }}
          aria-label="Upload Image File"
        />
      </label>

      {/* Binary Size Feedback */}
      <div style={{ marginTop: '10px', color: '#555' }}>
        Binary Size: <strong>{binarySize} bytes</strong>
      </div>
    </div>
  );
};


/////////////////////////////////////////////////


/**
 * ThemeImageCustom Component
 * Handles rendering the image and boundary box on the canvas.
 */
const ThemeImageCustom = ({ element, selectedElement, setSelectedElement, updateElement, render, editAllowed }) => {
  const [imageObj, setImageObj] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 100, height: 100 }); // Default size
  const imageRef = useRef();

  const isSelected = selectedElement && selectedElement.id === element.id;

  /**
   * Load and render the image from Base64.
   */
  useEffect(() => {
    if (element.imageData) {
      const img = new window.Image();
      img.src = element.imageData;
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        setImageObj(img);
        setImageSize({ width: img.width, height: img.height }); // Set real image size
      };
      img.onerror = () => toast.error('Failed to load image.');
    }
  }, [element.imageData]);

  /**
   * Handle drag end to update x and y positions.
   */
  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x));
    updateElement(element.id, 'y', Math.round(y));
  };

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
    >
      {/* Render the image if available */}
      {imageObj ? (
        <KonvaImage
          image={imageObj}
          width={imageSize.width}
          height={imageSize.height}
          ref={imageRef}
        />
      ) : (
        // Placeholder for dummy info when no image is uploaded
        <>
          <KonvaRect
            width={imageSize.width}
            height={imageSize.height}
            fill="#f0f0f0"
            stroke="#cccccc"
            strokeWidth={1}
            dash={[5, 5]}
          />
          <KonvaText
            text="No Image"
            fontSize={14}
            fill="#888888"
            x={10}
            y={10}
          />
          <KonvaText
            text="upload PNG or "
            fontSize={8}
            fill="#888888"
            x={10}
            y={30}
          />
          <KonvaText
            text="GIF (better & smaller !)"
            fontSize={8}
            fill="#888888"
            x={10}
            y={42}
          />
        </>
      )}

      {/* Render boundary box for selection */}
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={imageSize.width}
          height={imageSize.height}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [4, 4] : [0, 0]}
          listening={false}
        />
      )}
    </Group>
  );
};

ThemeImageCustom.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    imageData: PropTypes.string, // Base64 image data
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool.isRequired,
};

export default ThemeImageCustom;